import React, { useCallback, useEffect, useRef, useState } from 'react'
import o from '../../components/Overlay.module.css'
import pa from '../ProductActivityPage.module.css'
import styles from '../BatchesPage.module.css'
import { apiJson } from '../../lib/api'
import { useLayer } from '../../components/layers'
import { PendingButton, inertClass, useSlow } from '../../components/pending'
import { dayShort, kg } from '../productActivity/model'
import { ItemName } from '../productActivity/itemName'
import {
  ACTS,
  DEFAULT_REASON,
  actBody,
  corrections,
  historyRows,
  initialValues,
  localLabel,
  qcLine,
  spanLine,
  statusLine,
  statusOf,
  stackView,
  tempLine,
  weightTile,
  type ActKey,
  type Batch,
  type BinBrief,
  type DetailResponse,
  type Member,
} from './model'
import { openableRow } from '../../lib/openableRow'

// One batch (V4 bxDetail): what it holds, where it is in its life, and the
// audited corrections. Every correction is one POST that the API writes to
// the batch history; this modal never edits a figure itself.

const GREEN = { bg: '#e8efd6', fg: '#3f4a2c', sub: '#5a6b3f' }
const AMBER = { bg: '#fbeecd', fg: '#7a5410', sub: '#8a6420' }

type Tab = 'overview' | 'members' | 'history'

export function BatchDetail({
  user,
  batchId,
  onClose,
  onChanged,
  toast,
  onOpenBatch,
  onViewBin,
  onOpenCycle,
}: {
  user: any
  batchId: string
  onClose: () => void
  onChanged: () => void
  toast: (m: string) => void
  onOpenBatch: (id: string) => void
  onViewBin: (bin: string) => void
  onOpenCycle: (m: Member) => void
}) {
  const [data, setData] = useState<DetailResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [tab, setTab] = useState<Tab>('overview')
  const [act, setAct] = useState<ActKey | null>(null)
  const [v1, setV1] = useState('')
  const [v2, setV2] = useState('')
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [actError, setActError] = useState<string | null>(null)

  const userRef = useRef(user)
  userRef.current = user
  const load = useCallback(async () => {
    try {
      const r = await apiJson<DetailResponse>(userRef.current, `/api/batches/${encodeURIComponent(batchId)}`)
      setData(r)
      setError(null)
    } catch (e: any) {
      setError(e?.message || 'Could not load this batch')
    }
  }, [batchId])

  useEffect(() => {
    load()
  }, [load])

  // Two layers (h 425-458): the record, and the correction form in it.
  // Escape cancels the form first, as its Cancel does; the record closes
  // after, and asks first while the form holds a change nobody has applied.
  const panelEl = useRef<HTMLDivElement>(null)
  const actEl = useRef<HTMLDivElement>(null)
  const [actStart, setActStart] = useState<string[]>([])
  const actDirty = !!act && !busy && (v1 !== actStart[0] || v2 !== actStart[1] || reason !== actStart[2])
  const modal = useLayer(true, onClose, { dirty: actDirty, el: panelEl, modal: true, busy })
  useLayer(!!act, () => setAct(null), { el: actEl, focusFirst: true, busy })
  // A correction in flight (h 3984-3992): the record stays open and takes no
  // presses; it dims, and the button says so, once the wait is real.
  const slow = useSlow(busy)
  const inert = inertClass(busy, slow)

  const open = (b: Batch, k: ActKey) => {
    const init = initialValues(b, k)
    setAct(k)
    setV1(init[0])
    setV2(init[1])
    setReason(DEFAULT_REASON[k])
    setActStart([init[0], init[1], DEFAULT_REASON[k]])
    setActError(null)
  }

  const apply = async (b: Batch) => {
    if (!act) return
    const req = actBody(b, act, v1, v2, reason)
    if ('error' in req) {
      setActError(req.error)
      return
    }
    setBusy(true)
    setActError(null)
    try {
      const r = await apiJson<{ message: string; unchanged?: boolean }>(user, `/api/batches/${encodeURIComponent(b.id)}/${act}`, { body: req.body })
      toast(r.message)
      setAct(null)
      await load()
      onChanged()
    } catch (e: any) {
      setActError(e?.message || 'Could not record that correction')
    } finally {
      setBusy(false)
    }
  }

  const b = data?.batch
  return (
    <div className={o.scrim} onClick={modal.requestClose}>
      <div ref={panelEl} className={[o.modal, o.wide].join(' ')} role="dialog" aria-modal="true" aria-label={b ? 'Batch ' + b.batch_code : 'Batch'} onClick={(e) => e.stopPropagation()}>
        {!data || !b ? (
          <div className={o.head}>
            <div className={o.headText}>{error ? <div className={styles.badBox}>{error}</div> : <p className={o.sub}>Loading…</p>}</div>
            <button type="button" className={o.close} aria-label="Close" onClick={onClose}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.75" strokeLinecap="round">
                <path d="M18 6 6 18M6 6l12 12" />
              </svg>
            </button>
          </div>
        ) : (
          <>
            <Head b={b} onClose={modal.requestClose} />
            <div className={`${styles.dTabs} ${inert}`}>
              {(
                [
                  ['overview', 'Overview'],
                  ['members', 'Product activity'],
                  ['history', 'History'],
                ] as const
              ).map(([k, label]) => (
                <button key={k} type="button" className={tab === k ? pa.dTabOn : pa.dTab} onClick={() => setTab(k)}>
                  {label}
                  {k === 'members' ? ' · ' + data.members.length : ''}
                </button>
              ))}
            </div>
            <div className={`${styles.dBody} ${inert}`}>
              {tab === 'overview' && (
                <Overview
                  data={data}
                  onAct={(k) => open(b, k)}
                  onOpenBatch={onOpenBatch}
                  onViewBin={() =>
                    modal.guard(() => {
                      onClose()
                      onViewBin(b.bin_code || '')
                    })
                  }
                />
              )}
              {tab === 'members' && <Members members={data.members} onOpen={onOpenCycle} />}
              {tab === 'history' && <History b={b} data={data} />}
            </div>
            {act && (
              <div ref={actEl} className={styles.acts} data-testid="acts">
                <div className={styles.actsTitle}>{ACTS[act].title}</div>
                <div className={styles.actsNote}>{ACTS[act].note}</div>
                <div className={`${styles.actsFields} ${inert}`}>
                  {act === 'job' ? (
                    <div className={o.field}>
                      <label className={o.fieldLabel}>{ACTS.job.label}</label>
                      <select className={o.input} value={v1} onChange={(e) => setV1(e.target.value)} aria-label={ACTS.job.label}>
                        {!b.picking_job_id && <option value="">Pick a picking job</option>}
                        {data.picking_jobs.map((j) => (
                          <option key={j.id} value={j.id}>
                            {j.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  ) : (
                    ACTS[act].label && (
                      <div className={o.field}>
                        <label className={o.fieldLabel}>{ACTS[act].label}</label>
                        <input
                          className={o.input}
                          aria-label={ACTS[act].label}
                          inputMode={act === 'weight' || act === 'temperatures' ? 'decimal' : undefined}
                          value={v1}
                          onChange={(e) => setV1(e.target.value)}
                        />
                      </div>
                    )
                  )}
                  {act === 'temperatures' && (
                    <div className={o.field}>
                      <label className={o.fieldLabel}>Packhouse temperature (°C)</label>
                      <input className={o.input} aria-label="Packhouse temperature (°C)" inputMode="decimal" value={v2} onChange={(e) => setV2(e.target.value)} />
                    </div>
                  )}
                  <div className={o.field}>
                    <label className={o.fieldLabel}>Reason</label>
                    <input className={o.input} aria-label="Reason" value={reason} onChange={(e) => setReason(e.target.value)} />
                  </div>
                </div>
                {actError && (
                  <div className={styles.badBox} role="alert">
                    {actError}
                  </div>
                )}
                <div className={styles.actsBtns}>
                  <button type="button" className={o.ghost} onClick={() => setAct(null)} disabled={busy}>
                    Cancel
                  </button>
                  <PendingButton className={o.primary} busy={busy} pending="Applying…" onClick={() => apply(b)}>
                    Apply and record
                  </PendingButton>
                </div>
              </div>
            )}
          </>
        )}
      </div>
      {modal.confirm}
    </div>
  )
}

function Head({ b, onClose }: { b: Batch; onClose: () => void }) {
  const st = statusOf(b)
  const w = weightTile(b)
  const done = !!b.completed_at
  const rec = !!b.receipted_at
  const tile = (on: boolean) => (on ? GREEN : AMBER)
  return (
    <div className={styles.dHead}>
      <div className={styles.dTitleRow}>
        <div className={styles.dTitleCol}>
          <div className={styles.dTitle}>{b.batch_code}</div>
          <div className={styles.dSub}>
            {[b.product || '—', b.picking_job_name || '—', b.site_name || '—', 'bin ' + (b.bin_code || '—')].join(' · ')}
          </div>
        </div>
        <span className={styles.state} style={{ background: st.bg, color: st.fg }}>
          {st.label}
        </span>
        <button type="button" className={o.close} aria-label="Close" onClick={onClose}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.75" strokeLinecap="round">
            <path d="M18 6 6 18M6 6l12 12" />
          </svg>
        </button>
      </div>
      <div className={styles.dTiles}>
        <div className={styles.dTile}>
          <span className={styles.dTileLabel}>Items / cycles</span>
          <span className={styles.dTileValue}>{b.items}</span>
          <span className={styles.dTileSub}>{b.live === b.items ? 'all included' : b.live + ' included'}</span>
        </div>
        <div className={styles.dTile} data-testid="weight-tile">
          <span className={styles.dTileLabel}>{w.label}</span>
          <span className={styles.dTileValue}>{w.value}</span>
          <span className={styles.dTileSub}>{w.sub}</span>
        </div>
        <div className={styles.dTile} style={{ background: tile(done).bg, color: tile(done).fg }}>
          <span className={styles.dTileLabel}>Completed in field</span>
          <span className={styles.dTileState}>{b.completed_local ? localLabel(b.completed_local) : 'Not completed'}</span>
          <span className={styles.dTileSub} style={{ color: tile(done).sub }}>
            {done ? 'by ' + (b.completed_by || '—') : 'no field completion recorded'}
          </span>
        </div>
        <div className={styles.dTile} style={{ background: tile(rec).bg, color: tile(rec).fg }}>
          <span className={styles.dTileLabel}>Receipted</span>
          <span className={styles.dTileState}>{b.receipted_local ? localLabel(b.receipted_local) : 'Not receipted'}</span>
          <span className={styles.dTileSub} style={{ color: tile(rec).sub }}>
            {rec ? 'by ' + (b.receipted_by || '—') : 'bin QR not scanned in'}
          </span>
        </div>
      </div>
    </div>
  )
}

function Overview({ data, onAct, onOpenBatch, onViewBin }: { data: DetailResponse; onAct: (k: ActKey) => void; onOpenBatch: (id: string) => void; onViewBin: () => void }) {
  const b = data.batch
  const max = data.settings.max_bin_kg
  const fills: [string, BinBrief][] = []
  if (data.bin_fills.previous) fills.push(['Previous fill', data.bin_fills.previous])
  if (data.bin_fills.next) fills.push(['Next fill', data.bin_fills.next])
  const meta: [string, string][] = [
    ['Bin', b.bin_code || '—'],
    ['Grower / orchard', b.site_name || '—'],
    ['Rows picked from', b.blocks.length ? b.blocks.join(', ') : '—'],
    ['Spans', spanLine(b.dates)],
    ['Pick temperature', tempLine(b.pick_temp_c)],
    ['Packhouse temperature', tempLine(b.packhouse_temp_c)],
  ]
  return (
    <>
      <div className={styles.dLine}>{statusLine(b)}</div>
      {b.exceptions.includes('receipted_never_completed') && (
        <div className={styles.warnBox}>
          The bin QR was receipted at the packhouse but the field station never recorded an End Batch. Nothing is lost, the cycles are all here, but the field completion
          time is unknown.
        </div>
      )}
      {b.over && b.over_by !== null && max !== null && (
        <div className={styles.warnBox} data-testid="over-box">
          This bin is {b.over_by.toFixed(1)} kg over the {max} kg limit set for this subscription. It holds {b.in_bin_kg.toFixed(1)} kg. Staff may be overfilling, which crushes
          fruit at the bottom.
        </div>
      )}
      {b.excluded && (
        <div className={styles.mutedBox}>
          Every cycle in this batch is excluded, so the batch contributes nothing to production. Include a cycle again from Product activity and the batch counts again.
        </div>
      )}
      <div className={styles.meta}>
        {meta.map(([k, v]) => (
          <div key={k} className={styles.metaItem}>
            <span className={styles.metaLabel}>{k}</span>
            <span className={styles.metaValue}>{v}</span>
          </div>
        ))}
      </div>
      <div className={styles.dLine}>{qcLine(b)}</div>
      {data.weighing && data.weighing.bins.length > 1 && <StackBlock w={stackView(data.weighing, b.id)} onOpenBatch={onOpenBatch} />}
      {fills.length > 0 && (
        <>
          <div className={styles.sectionTitle}>Other fills of bin {b.bin_code}</div>
          <div className={styles.fills}>
            {fills.map(([rel, f]) => (
              <button key={f.id} type="button" className={styles.fill} onClick={() => onOpenBatch(f.id)}>
                <span className={styles.fillRel}>{rel}</span>
                <span className={styles.fillCode}>{f.batch_code}</span>
                <span className={styles.fillSub}>
                  {(f.product || '—') + ' · ' + (f.first_date ? dayShort(f.first_date) : '—') + ' · ' + f.in_bin_kg.toFixed(1) + ' kg'}
                </span>
              </button>
            ))}
          </div>
          {data.bin_fills.count > 3 && (
            <button type="button" className={styles.linkBtn} onClick={onViewBin}>
              View all {data.bin_fills.count} fills of bin {b.bin_code}
            </button>
          )}
        </>
      )}
      <div className={styles.fix}>
        <div>
          <div className={styles.fixTitle}>Corrections and recovery</div>
          <div className={styles.fixText}>Actions on this bin. Each one asks for a reason and is written to the batch history.</div>
        </div>
        <div className={styles.fixBtns}>
          {corrections(b).map((c) => (
            <button key={c.key} type="button" className={o.ghost} onClick={() => onAct(c.key)} style={{ height: '2rem', padding: '0 1rem', fontSize: '0.8125rem' }}>
              {c.label}
            </button>
          ))}
        </div>
        {!b.excluded && (
          <>
            <div className={styles.fixRule} />
            <div className={styles.dangerRow}>
              <button type="button" className={styles.dangerBtn} onClick={() => onAct('exclude')}>
                Exclude batch
              </button>
              <span className={styles.fixText}>Removes the batch from production, weight and reporting.</span>
            </div>
          </>
        )}
        <div className={styles.moveNote}>
          Moving cycles between batches happens in Product Activity. Select the cycles and set their batch there, so the move is audited on each cycle.
        </div>
      </div>
    </>
  )
}

function Members({ members, onOpen }: { members: Member[]; onOpen: (m: Member) => void }) {
  return (
    <>
      <div className={styles.dLine}>
        These are the Product Activity cycles this batch is made of. The batch weight is the weighed figure where the bin has one, otherwise the gross sum of these cycles;
        credited weight is what is left after quality. Excluded cycles count as zero in both.
      </div>
      {members.length === 0 ? (
        <div className={pa.empty}>No cycles in this batch.</div>
      ) : (
        <div role="table" aria-label="Cycles in this batch">
          <div className={styles.mHead} role="row">
            {['Time', 'Item', 'Worker', 'Location', 'Gross', 'Credited'].map((h) => (
              <span key={h} role="columnheader">
                {h}
              </span>
            ))}
          </div>
          {members.map((m) => (
            <div
              key={m.id}
              role="row"
              data-testid="member-row"
              className={[styles.mRow, m.status === 'excluded' ? styles.mRowExcluded : ''].join(' ')}
              title="Open this cycle in Product activity"
              {...openableRow(() => onOpen(m))}
            >
              <span className={styles.stack}>
                <span className={pa.whenTime}>{m.eff_time || '—'}</span>
                <span className={styles.sub}>{dayShort(m.work_date)}</span>
              </span>
              <span className={pa.item}><ItemName c={m} /></span>
              <span className={m.worker_name ? pa.cell : pa.cellBad}>{m.worker_name || 'Unassigned'}</span>
              <span className={pa.cellMuted}>{m.location || '—'}</span>
              <span className={pa.num}>{kg(m.gross_kg)}</span>
              <span className={m.qc_factor !== null ? pa.numCredited : pa.num}>
                {kg(m.credited_kg)}
                {m.set_by_bin && <span className={styles.sub}> set by bin</span>}
              </span>
            </div>
          ))}
        </div>
      )}
    </>
  )
}

// V4 "How this weight was worked out": a bin weighed on a Crew stack. The
// open bin's row is highlighted; the others open their batch.
function StackBlock({ w, onOpenBatch }: { w: ReturnType<typeof stackView>; onOpenBatch: (id: string) => void }) {
  return (
    <div className={styles.stack} data-testid="stack-block">
      <div className={styles.stackHeadWrap}>
        <div className={styles.sectionTitle}>How this weight was worked out</div>
        <div className={styles.stackHead}>{w.head}</div>
      </div>
      <div className={styles.stackText}>{w.sum}</div>
      <div className={styles.stackTable} role="table" aria-label="Bins on this weighing">
        <div className={styles.stackCols} role="row">
          {['Bin', 'Empty weight', 'Field estimate', 'Share', 'Weight given'].map((h) => (
            <span key={h} role="columnheader">
              {h}
            </span>
          ))}
        </div>
        {w.rows.map((r) => {
          const cells = (
            <>
              <span className={styles.stackBin} role="cell">
                {r.bin} <span className={styles.stackCode}>· {r.code}</span>
              </span>
              <span role="cell">{r.tare}</span>
              <span role="cell">{r.est}</span>
              <span role="cell">{r.pct}</span>
              <span role="cell">{r.share}</span>
            </>
          )
          return r.mine ? (
            <div key={r.id} className={styles.stackMine} role="row" aria-current="true">
              {cells}
            </div>
          ) : (
            <button key={r.id} type="button" className={styles.stackRow} role="row" onClick={() => onOpenBatch(r.id)}>
              {cells}
            </button>
          )
        })}
      </div>
      {w.mine && <div className={styles.stackText}>{w.mine}</div>}
    </div>
  )
}

function History({ b, data }: { b: Batch; data: DetailResponse }) {
  const rows = historyRows(b, data.history)
  if (!rows.length) return <div className={pa.empty}>Nothing recorded against this batch yet.</div>
  return (
    <div>
      {rows.map((h) => (
        <div key={h.id} className={styles.hRow} data-testid="history-row">
          <span className={styles.hDot} style={{ background: h.manual ? 'var(--dp-accent)' : '#c3bcae' }} />
          <span className={styles.hTime}>{h.t}</span>
          <span className={styles.hMain}>
            <span className={styles.hKind}>{h.kind}</span>
            <span className={styles.hWhat}>{h.what}</span>
            {h.why && <span className={styles.hWhy}>{h.why}</span>}
          </span>
          <span className={styles.hWho}>{h.who}</span>
        </div>
      ))}
    </div>
  )
}
